import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Bell, CheckCircle2, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const PushNotificationTest = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tokenCount, setTokenCount] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testTitle, setTestTitle] = useState('Test Notification 🔔');
  const [testBody, setTestBody] = useState('This is a test push notification from Petius!');

  const checkTokens = async () => {
    setLoading(true);
    try {
      const { count, error } = await supabase
        .from('user_push_tokens')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user?.id);

      if (error) throw error;
      setTokenCount(count ?? 0);
    } catch (error: any) {
      console.error('Error checking tokens:', error);
      setTestResult({ success: false, message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    if (!user) return;

    setLoading(true);
    setTestResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('send-push', {
        body: {
          user_id: user.id,
          title: testTitle,
          body: testBody,
          data: {
            type: 'test',
            test: true
          }
        }
      });

      if (error) throw error;

      setTestResult({
        success: true,
        message: 'Test notification sent successfully! Check your device.'
      });
    } catch (error: any) {
      console.error('Error sending test notification:', error);
      setTestResult({
        success: false,
        message: `Failed to send: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const checkPermissions = () => {
    if (!('Notification' in window)) {
      setTestResult({
        success: false,
        message: 'Push notifications not supported in this browser'
      });
      return;
    }

    const permission = Notification.permission;
    setTestResult({
      success: permission === 'granted',
      message: `Notification permission: ${permission}`
    });
  };

  return (
    <Card className="p-6 max-w-2xl mx-auto">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-blue-500" />
          <div>
            <h2 className="text-2xl font-bold">Push Notification Testing</h2>
            <p className="text-sm text-muted-foreground">
              Verify push notification functionality
            </p>
          </div>
        </div>

        {/* Testing Checklist */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg">Testing Checklist:</h3>
          
          {/* 1. Check Tokens */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex-1">
              <p className="font-medium">1. Check Push Tokens</p>
              <p className="text-sm text-muted-foreground">
                Verify tokens are registered in database
              </p>
            </div>
            <div className="flex items-center gap-2">
              {tokenCount !== null && (
                <Badge variant={tokenCount > 0 ? 'default' : 'destructive'}>
                  {tokenCount} token{tokenCount !== 1 ? 's' : ''}
                </Badge>
              )}
              <Button
                onClick={checkTokens}
                disabled={loading}
                size="sm"
                variant="outline"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
              </Button>
            </div>
          </div>

          {/* 2. Check Permissions */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex-1">
              <p className="font-medium">2. Check Browser Permissions</p>
              <p className="text-sm text-muted-foreground">
                Verify notification permissions are granted
              </p>
            </div>
            <Button
              onClick={checkPermissions}
              size="sm"
              variant="outline"
            >
              Check
            </Button>
          </div>

          {/* 3. Test send-push Function */}
          <div className="space-y-3 p-3 border rounded-lg">
            <div>
              <p className="font-medium">3. Test send-push Edge Function</p>
              <p className="text-sm text-muted-foreground">
                Send a test notification to your device
              </p>
            </div>

            <div className="space-y-2">
              <div>
                <Label htmlFor="test-title">Notification Title</Label>
                <Input
                  id="test-title"
                  value={testTitle}
                  onChange={(e) => setTestTitle(e.target.value)}
                  placeholder="Test Notification"
                />
              </div>

              <div>
                <Label htmlFor="test-body">Notification Body</Label>
                <Input
                  id="test-body"
                  value={testBody}
                  onChange={(e) => setTestBody(e.target.value)}
                  placeholder="This is a test message"
                />
              </div>

              <Button
                onClick={sendTestNotification}
                disabled={loading || !user}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Bell className="h-4 w-4 mr-2" />
                    Send Test Notification
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* 4. Check Logs */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex-1">
              <p className="font-medium">4. Review Edge Function Logs</p>
              <p className="text-sm text-muted-foreground">
                Check Supabase dashboard for errors
              </p>
            </div>
            <a
              href="https://supabase.com/dashboard/project/bagohvtkkscmmxhkxtdc/functions/send-push/logs"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="sm" variant="outline">
                View Logs
              </Button>
            </a>
          </div>
        </div>

        {/* Test Result */}
        {testResult && (
          <Alert className={testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {testResult.success ? (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            ) : (
              <XCircle className="h-4 w-4 text-red-600" />
            )}
            <AlertDescription className={testResult.success ? 'text-green-800' : 'text-red-800'}>
              {testResult.message}
            </AlertDescription>
          </Alert>
        )}

        {/* Info Box */}
        <Alert className="bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800 text-sm">
            <strong>Note:</strong> Push notifications require:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>User permission granted in browser/app</li>
              <li>Valid push token registered in database</li>
              <li>VAPID keys configured in edge function</li>
              <li>Service worker registered (for web)</li>
            </ul>
          </AlertDescription>
        </Alert>
      </div>
    </Card>
  );
};
